import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Divider,
    Grid,
    Chip,
    Avatar,
    Modal,
    ModalDialog,
    DialogTitle,
    DialogContent,
    Button,
    Input,
    Textarea,
    List,
    ListItem,
    ListItemButton,
    ListItemContent
} from '@mui/joy';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import EmailIcon from '@mui/icons-material/Email';
import HomeIcon from '@mui/icons-material/Home';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import BadgeIcon from '@mui/icons-material/Badge';
import axiosConfig from '../util/axiosConfig';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import { useNavigate } from 'react-router-dom';

const Profile = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [profileData, setProfileData] = useState(null);
    const [photoUrl, setPhotoUrl] = useState(null);
    const [canEdit, setCanEdit] = useState(false);
    const [editExpiresAt, setEditExpiresAt] = useState(null);
    const [showEditInfo, setShowEditInfo] = useState(false);
    // Inline edit removed; dedicated edit page is available at /profile/edit
    const isEditing = false;
    const formData = {};
    const handleChange = () => () => { };
    const navigate = useNavigate();

    // College selection modal state
    const [collegeModalOpen, setCollegeModalOpen] = useState(false);
    const [collegeType, setCollegeType] = useState(null); // 'ug' or 'pg'
    const [collegeSearchTerm, setCollegeSearchTerm] = useState('');
    const [collegeSearchResults, setCollegeSearchResults] = useState([]);
    const [collegeSearchLoading, setCollegeSearchLoading] = useState(false);
    const [collegeUpdateLoading, setCollegeUpdateLoading] = useState(false);
    const [collegeSearchOffset, setCollegeSearchOffset] = useState(0);
    const [collegeHasMore, setCollegeHasMore] = useState(false);
    const [collegeLoadingMore, setCollegeLoadingMore] = useState(false);

    useEffect(() => {
        fetchProfile();
    }, []);

    // Cleanup effect to revoke blob URL when component unmounts or photoUrl changes
    useEffect(() => {
        return () => {
            if (photoUrl && photoUrl.startsWith('blob:')) {
                URL.revokeObjectURL(photoUrl);
            }
        };
    }, [photoUrl]);

    const fetchProfile = async () => {
        try {
            setError(null);
            const response = await axiosConfig.get('/students/profile');
            setProfileData(response.data);
            // Fetch photo after profile data is loaded
            fetchPhoto();
            //        fetchEditPermission();
        } catch (err) {
            console.error('Error fetching profile:', err);
            setError(err.response?.data?.errorMessage || 'Failed to load profile data');
        } finally {
            setLoading(false);
        }
    };

    const fetchPhoto = async () => {
        try {
            const response = await axiosConfig.get('/students/profile-photo', {
                responseType: 'blob' // Important: fetch as blob for binary data
            });

            // Create a blob URL from the response
            const blobUrl = URL.createObjectURL(response.data);
            setPhotoUrl(blobUrl);
        } catch (err) {
            // Photo might not exist, which is okay
            // Don't set error, just leave photoUrl as null
            console.log('Photo not available or error fetching photo:', err.response?.status);
        }
    };

    // const fetchEditPermission = async () => {
    //     try {
    //         const resp = await axiosConfig.get('/students/profile/edit-permission');
    //         setCanEdit(Boolean(resp.data?.canEdit));
    //         setEditExpiresAt(resp.data?.expiresAt || null);
    //     } catch (e) {
    //         // ignore
    //     }
    // };

    // College search with debouncing
    const searchColleges = useCallback(async (searchTerm, offset = 0, append = false) => {
        if (!searchTerm || searchTerm.trim().length === 0) {
            setCollegeSearchResults([]);
            setCollegeHasMore(false);
            setCollegeSearchOffset(0);
            return;
        }

        if (offset === 0) {
            setCollegeSearchLoading(true);
        } else {
            setCollegeLoadingMore(true);
        }

        try {
            const response = await axiosConfig.get('/students/colleges/search', {
                params: { q: searchTerm, offset: offset }
            });

            if (append) {
                setCollegeSearchResults(prev => [...prev, ...(response.data.results || [])]);
            } else {
                setCollegeSearchResults(response.data.results || []);
            }
            setCollegeHasMore(response.data.hasMore || false);
            setCollegeSearchOffset(offset);
        } catch (err) {
            console.error('Error searching colleges:', err);
            if (!append) {
                setCollegeSearchResults([]);
            }
            setCollegeHasMore(false);
        } finally {
            setCollegeSearchLoading(false);
            setCollegeLoadingMore(false);
        }
    }, []);

    // Load more colleges (infinite scroll)
    const loadMoreColleges = useCallback(() => {
        if (!collegeSearchLoading && !collegeLoadingMore && collegeHasMore && collegeSearchTerm) {
            const nextOffset = collegeSearchOffset + 50;
            searchColleges(collegeSearchTerm, nextOffset, true);
        }
    }, [collegeSearchTerm, collegeSearchOffset, collegeHasMore, collegeSearchLoading, collegeLoadingMore, searchColleges]);

    // Debounced search effect
    useEffect(() => {
        const timer = setTimeout(() => {
            if (collegeModalOpen) {
                setCollegeSearchOffset(0);
                searchColleges(collegeSearchTerm, 0, false);
            }
        }, 300); // 300ms debounce

        return () => clearTimeout(timer);
    }, [collegeSearchTerm, collegeModalOpen, searchColleges]);

    // Open college selection modal
    const handleOpenCollegeModal = (type) => {
        setCollegeType(type);
        setCollegeSearchTerm('');
        setCollegeSearchResults([]);
        setCollegeSearchOffset(0);
        setCollegeHasMore(false);
        setCollegeModalOpen(true);
    };

    // Close college selection modal
    const handleCloseCollegeModal = () => {
        setCollegeModalOpen(false);
        setCollegeType(null);
        setCollegeSearchTerm('');
        setCollegeSearchResults([]);
        setCollegeSearchOffset(0);
        setCollegeHasMore(false);
    };

    // Handle scroll for infinite loading
    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        // Load more when user scrolls to within 100px of bottom
        if (scrollHeight - scrollTop - clientHeight < 100) {
            loadMoreColleges();
        }
    };

    // Handle college selection and update
    const handleSelectCollege = async (college) => {
        setCollegeUpdateLoading(true);
        try {
            await axiosConfig.put('/students/profile/college-code', {
                collegeCode: college.collegecode,
                type: collegeType
            });

            // Refresh profile data
            await fetchProfile();

            handleCloseCollegeModal();

            // Show success message (you can use a toast/notification here)
            setError(null);
        } catch (err) {
            console.error('Error updating college:', err);
            setError(err.response?.data?.errorMessage || 'Failed to update college');
        } finally {
            setCollegeUpdateLoading(false);
        }
    };

    // // No inline editing functions; navigation to edit page is used

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    gap: 2
                }}
            >
                <CircularProgress size="lg" />
                <Typography level="body-md">Loading profile data...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert color="danger" variant="soft">
                    {error}
                </Alert>
            </Box>
        );
    }

    if (!profileData) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert color="warning" variant="soft">
                    No profile data available
                </Alert>
            </Box>
        );
    }

    // Helper component for displaying field
    const FieldDisplay = ({ label, value, icon }) => {
        if (!value || value === "" || value === "0" || value === 0) return null;
        return (
            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    {icon}
                    <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium' }}>
                        {label}
                    </Typography>
                </Box>
                <Typography level="body-lg" sx={{ ml: 4, color: '#404041' }}>
                    {value}
                </Typography>
            </Box>
        );
    };

    // Helper component for section title
    const SectionTitle = ({ title, icon }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {icon}
            <Typography level="title-lg" sx={{ color: '#eb7a24', fontWeight: 'bold' }}>
                {title}
            </Typography>
        </Box>
    );

    return (
        <Box sx={{ p: 2, maxWidth: '1400px', mx: 'auto' }}>
            {/* Edit permission status
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, gap: 1 }}>
                {canEdit && (
                    <Chip startDecorator={<EditIcon />} color="success" variant="soft">
                        Profile Editing enabled{editExpiresAt ? ` until ${new Date(editExpiresAt).toLocaleString()}` : ''}
                    </Chip>
                )}
                {canEdit && (
                    <Button onClick={() => navigate('/profile/edit')} startDecorator={<EditIcon />} sx={{ backgroundColor: '#eb7a24', '&:hover': { backgroundColor: '#e55125' } }}>Edit Profile</Button>
                )}
            </Box>
            {/* Personal Information Card */
            }
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent sx={{ p: 3 }}>
                    <SectionTitle title="Personal Information" icon={<PersonIcon sx={{ color: '#eb7a24' }} />} />
                    <Grid container spacing={3}>
                        {/* Student Photo */}
                        <Grid xs={12} sm={6} md={4}>
                            <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Avatar
                                    src={photoUrl || undefined}
                                    alt={profileData.name || 'Student Photo'}
                                    sx={{
                                        width: '100%',
                                        height: 225,
                                        borderRadius: 10,
                                        border: '3px solid #eb7a24',
                                        mb: 1,
                                        bgcolor: '#f19b30',
                                        fontSize: '3rem'
                                    }}
                                >
                                    {profileData.name ? profileData.name.charAt(0).toUpperCase() : 'S'}
                                </Avatar>
                                <Typography level="body-sm" sx={{ color: '#606162' }}>
                                    Student Photo
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid xs={12} sm={6} md={4}>
                            {isEditing ? (
                                <Box sx={{ mb: 2 }}>
                                    <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Name</Typography>
                                    <Input value={formData.name ?? ''} onChange={handleChange('name')} placeholder="Name" />
                                </Box>
                            ) : (
                                <FieldDisplay label="Name" value={profileData.name} icon={<PersonIcon sx={{ fontSize: '18px', color: '#6e7072' }} />} />
                            )}
                        </Grid>
                        <Grid xs={12} sm={6} md={4}>
                            {isEditing ? (
                                <Box sx={{ mb: 2 }}>
                                    <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Gender</Typography>
                                    <Input value={formData.gender ?? ''} onChange={handleChange('gender')} placeholder="Gender" />
                                </Box>
                            ) : (
                                <FieldDisplay label="Gender" value={profileData.gender} icon={<PersonIcon sx={{ fontSize: '18px', color: '#6e7072' }} />} />
                            )}
                        </Grid>
                        <Grid xs={12} sm={6} md={4}>
                            {isEditing ? (
                                <Box sx={{ mb: 2 }}>
                                    <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Date of Birth</Typography>
                                    <Input value={formData.dateofbirth ?? ''} onChange={handleChange('dateofbirth')} placeholder="dd/MM/yyyy" />
                                </Box>
                            ) : (
                                <FieldDisplay label="Date of Birth" value={profileData.dateofbirth} icon={<CalendarTodayIcon sx={{ fontSize: '18px', color: '#6e7072' }} />} />
                            )}
                        </Grid>
                        {/* <Grid xs={12} sm={6} md={4}>
                            {isEditing ? (
                                <Box sx={{ mb: 2 }}>
                                    <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Hall Ticket</Typography>
                                    <Input value={formData.hallticket ?? ''} onChange={handleChange('hallticket')} placeholder="Hall Ticket" />
                                </Box>
                            ) : (
                                <FieldDisplay label="Hall Ticket" value={profileData.hallticket} icon={<BadgeIcon sx={{ fontSize: '18px', color: '#6e7072' }} />} />
                            )}
                        </Grid> */}
                        <Grid xs={12} sm={6} md={4}>
                            {isEditing ? (
                                <Box sx={{ mb: 2 }}>
                                    <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Blood Group</Typography>
                                    <Input value={formData.bgroup ?? ''} onChange={handleChange('bgroup')} placeholder="Blood Group" />
                                </Box>
                            ) : (
                                <FieldDisplay label="Blood Group" value={profileData.bgroup} icon={<PersonIcon sx={{ fontSize: '18px', color: '#6e7072' }} />} />
                            )}
                        </Grid>
                        <Grid xs={12} sm={6} md={4}>
                            {isEditing ? (
                                <Box sx={{ mb: 2 }}>
                                    <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Join Date</Typography>
                                    <Input value={formData.joindate ?? ''} onChange={handleChange('joindate')} placeholder="dd/MM/yyyy" />
                                </Box>
                            ) : (
                                <FieldDisplay label="Join Date" value={profileData.joindate} icon={<CalendarTodayIcon sx={{ fontSize: '18px', color: '#6e7072' }} />} />
                            )}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Contact Information Card */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent sx={{ p: 3 }}>
                    <SectionTitle title="Contact Information" icon={<ContactPhoneIcon sx={{ color: '#eb7a24' }} />} />
                    <Grid container spacing={3}>
                        <Grid xs={12} sm={6} md={4}>
                            {isEditing ? (
                                <Box sx={{ mb: 2 }}>
                                    <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Email</Typography>
                                    <Input value={formData.email} onChange={handleChange('email')} placeholder="Email" />
                                </Box>
                            ) : (
                                <FieldDisplay label="Email" value={profileData.email} icon={<EmailIcon sx={{ fontSize: '18px', color: '#6e7072' }} />} />
                            )}
                        </Grid>
                        <Grid xs={12} sm={6} md={4}>
                            {isEditing ? (
                                <Box sx={{ mb: 2 }}>
                                    <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Mobile</Typography>
                                    <Input value={formData.mobileno} onChange={handleChange('mobileno')} placeholder="Mobile" />
                                </Box>
                            ) : (
                                <FieldDisplay label="Mobile" value={profileData.mobileno} icon={<ContactPhoneIcon sx={{ fontSize: '18px', color: '#6e7072' }} />} />
                            )}
                        </Grid>
                        <Grid xs={12} sm={6} md={4}>
                            {isEditing ? (
                                <Box sx={{ mb: 2 }}>
                                    <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Landline</Typography>
                                    <Input value={formData.landline} onChange={handleChange('landline')} placeholder="Landline" />
                                </Box>
                            ) : (
                                <FieldDisplay label="Landline" value={profileData.landline} icon={<ContactPhoneIcon sx={{ fontSize: '18px', color: '#6e7072' }} />} />
                            )}
                        </Grid>
                        <Grid xs={12} sm={6} md={4}>
                            {isEditing ? (
                                <Box sx={{ mb: 2 }}>
                                    <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Father's Name</Typography>
                                    <Input value={formData.fathername} onChange={handleChange('fathername')} placeholder="Father's Name" />
                                </Box>
                            ) : (
                                <FieldDisplay label="Father's Name" value={profileData.fathername} icon={<PersonIcon sx={{ fontSize: '18px', color: '#6e7072' }} />} />
                            )}
                        </Grid>
                        <Grid xs={12} sm={6} md={4}>
                            {isEditing ? (
                                <Box sx={{ mb: 2 }}>
                                    <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Father's Contact</Typography>
                                    <Input value={formData.fathercontact} onChange={handleChange('fathercontact')} placeholder="Father's Contact" />
                                </Box>
                            ) : (
                                <FieldDisplay label="Father's Contact" value={profileData.fathercontact} icon={<ContactPhoneIcon sx={{ fontSize: '18px', color: '#6e7072' }} />} />
                            )}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Address Information Card */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent sx={{ p: 3 }}>
                    <SectionTitle title="Address Information" icon={<HomeIcon sx={{ color: '#eb7a24' }} />} />
                    <Grid container spacing={3}>
                        <Grid xs={12} md={6}>
                            {isEditing ? (
                                <Box sx={{ mb: 2 }}>
                                    <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Permanent Address</Typography>
                                    <Textarea minRows={3} value={formData.address} onChange={handleChange('address')} placeholder="Permanent Address" />
                                </Box>
                            ) : (
                                <FieldDisplay label="Permanent Address" value={profileData.address} icon={<HomeIcon sx={{ fontSize: '18px', color: '#6e7072' }} />} />
                            )}
                        </Grid>
                        <Grid xs={12} md={6}>
                            {isEditing ? (
                                <Box sx={{ mb: 2 }}>
                                    <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Local Address</Typography>
                                    <Textarea minRows={3} value={formData.laddress} onChange={handleChange('laddress')} placeholder="Local Address" />
                                </Box>
                            ) : (
                                <FieldDisplay label="Local Address" value={profileData.laddress} icon={<HomeIcon sx={{ fontSize: '18px', color: '#6e7072' }} />} />
                            )}
                        </Grid>
                        <Grid xs={12} sm={6} md={4}>
                            {isEditing ? (
                                <Box sx={{ mb: 2 }}>
                                    <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>City</Typography>
                                    <Input value={formData.city} onChange={handleChange('city')} placeholder="City" />
                                </Box>
                            ) : (
                                <FieldDisplay label="City" value={profileData.city} icon={<HomeIcon sx={{ fontSize: '18px', color: '#6e7072' }} />} />
                            )}
                        </Grid>
                        <Grid xs={12} sm={6} md={4}>
                            {isEditing ? (
                                <Box sx={{ mb: 2 }}>
                                    <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>State</Typography>
                                    <Input value={formData.state} onChange={handleChange('state')} placeholder="State" />
                                </Box>
                            ) : (
                                <FieldDisplay label="State" value={profileData.state} icon={<HomeIcon sx={{ fontSize: '18px', color: '#6e7072' }} />} />
                            )}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Academic Qualifications */}
            {profileData.qualification && (
                <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <SectionTitle title="Academic Qualifications" icon={<SchoolIcon sx={{ color: '#eb7a24' }} />} />

                        {/* 10th Class */}
                        {(profileData.tenthpercentage || profileData.xboard || profileData.xyop || isEditing) && (
                            <Box sx={{ mb: 3 }}>
                                <Typography level="title-md" sx={{ color: '#404041', mb: 2, fontWeight: 'bold' }}>
                                    10th Class
                                </Typography>
                                <Grid container spacing={3}>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Board</Typography>
                                                <Input value={formData.xboard ?? ''} onChange={handleChange('xboard')} placeholder="Board" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Board" value={profileData.xboard} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Year of Passing</Typography>
                                                <Input value={formData.xyop ?? ''} onChange={handleChange('xyop')} placeholder="Year" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Year of Passing" value={profileData.xyop} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Percentage</Typography>
                                                <Input value={formData.tenthpercentage ?? ''} onChange={handleChange('tenthpercentage')} placeholder="Percentage" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Percentage" value={profileData.tenthpercentage ? `${profileData.tenthpercentage}%` : null} />
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {/* 12th Class / Intermediate */}
                        {(profileData.interpercentage || profileData.xiiboard || profileData.xiiyop || isEditing) && (
                            <Box sx={{ mb: 3 }}>
                                <Divider sx={{ my: 2 }} />
                                <Typography level="title-md" sx={{ color: '#404041', mb: 2, fontWeight: 'bold' }}>
                                    12th Class / Intermediate
                                </Typography>
                                <Grid container spacing={3}>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Board</Typography>
                                                <Input value={formData.xiiboard ?? ''} onChange={handleChange('xiiboard')} placeholder="Board" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Board" value={profileData.xiiboard} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Year of Passing</Typography>
                                                <Input value={formData.xiiyop ?? ''} onChange={handleChange('xiiyop')} placeholder="Year" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Year of Passing" value={profileData.xiiyop} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Percentage</Typography>
                                                <Input value={formData.interpercentage ?? ''} onChange={handleChange('interpercentage')} placeholder="Percentage" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Percentage" value={profileData.interpercentage ? `${profileData.interpercentage}%` : null} />
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {/* Diploma */}
                        {(profileData.diplomapercentage !== "0.00" || profileData.diplomaboard || profileData.diplomayop || isEditing) && (
                            <Box sx={{ mb: 3 }}>
                                <Divider sx={{ my: 2 }} />
                                <Typography level="title-md" sx={{ color: '#404041', mb: 2, fontWeight: 'bold' }}>
                                    Diploma
                                </Typography>
                                <Grid container spacing={3}>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Board</Typography>
                                                <Input value={formData.diplomaboard ?? ''} onChange={handleChange('diplomaboard')} placeholder="Board" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Board" value={profileData.diplomaboard} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Year of Passing</Typography>
                                                <Input value={formData.diplomayop ?? ''} onChange={handleChange('diplomayop')} placeholder="Year" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Year of Passing" value={profileData.diplomayop} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Percentage</Typography>
                                                <Input value={formData.diplomapercentage ?? ''} onChange={handleChange('diplomapercentage')} placeholder="Percentage" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Percentage" value={profileData.diplomapercentage !== "0.00" ? `${profileData.diplomapercentage}%` : null} />
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {/* UG Details */}
                        {(profileData.ugcollege || profileData.ugboard || profileData.ugspec || isEditing) && (
                            <Box sx={{ mb: 3 }}>
                                <Divider sx={{ my: 2 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography level="title-md" sx={{ color: '#404041', fontWeight: 'bold' }}>
                                        Under Graduation (UG)
                                    </Typography>
                                    {!profileData.ugcollegecode && (
                                        <Button
                                            size="sm"
                                            variant="outlined"
                                            color="primary"
                                            startDecorator={<SchoolIcon />}
                                            onClick={() => handleOpenCollegeModal('ug')}
                                            sx={{
                                                borderColor: '#eb7a24',
                                                color: '#eb7a24',
                                                '&:hover': {
                                                    borderColor: '#e55125',
                                                    backgroundColor: '#f19b30',
                                                    color: 'white'
                                                }
                                            }}
                                        >
                                            Update College
                                        </Button>
                                    )}
                                </Box>
                                <Grid container spacing={3}>
                                    <Grid xs={12} md={6}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Specialization</Typography>
                                                <Input value={formData.ugspecialization ?? formData.ugspec ?? ''} onChange={handleChange('ugspecialization')} placeholder="UG Specialization" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Specialization" value={profileData.ugspec} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} md={6}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Board/University</Typography>
                                                <Input value={formData.ugboard ?? ''} onChange={handleChange('ugboard')} placeholder="UG Board / University" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Board/University" value={profileData.ugboard} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} md={6}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>College</Typography>
                                                <Input value={formData.ugcollege ?? ''} onChange={handleChange('ugcollege')} placeholder="UG College" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="College" value={profileData.ugcollege} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} md={6}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>College Address</Typography>
                                                <Textarea minRows={2} value={formData.ugcollegeaddr ?? formData.ugcolladdr ?? ''} onChange={handleChange('ugcollegeaddr')} placeholder="UG College Address" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="College Address" value={profileData.ugcolladdr} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>City</Typography>
                                                <Input value={formData.ugcity ?? ''} onChange={handleChange('ugcity')} placeholder="City" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="City" value={profileData.ugcity} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>State</Typography>
                                                <Input value={formData.ugstate ?? ''} onChange={handleChange('ugstate')} placeholder="State" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="State" value={profileData.ugstate} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>District</Typography>
                                                <Input value={formData.ugcollegedistrict ?? ''} onChange={handleChange('ugcollegedistrict')} placeholder="District" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="District" value={profileData.ugcollegedistrict} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Year of Passing</Typography>
                                                <Input value={formData.ugyop ?? ''} onChange={handleChange('ugyop')} placeholder="Year" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Year of Passing" value={profileData.ugyop} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>History of UG Backlogs</Typography>
                                                <Input value={formData.tbacklog ?? formData.tugbacklog ?? ''} onChange={handleChange('tbacklog')} placeholder="Total UG Backlogs" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="History of UG Backlogs" value={profileData.tugbacklog ? `${profileData.tugbacklog}` : null} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Site</Typography>
                                                <Input value={formData.ugcollegesite ?? formData.ugsite ?? ''} onChange={handleChange('ugcollegesite')} placeholder="UG College Site" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Site" value={profileData.ugsite} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Certificate %</Typography>
                                                <Input value={formData.ugcertper ?? ''} onChange={handleChange('ugcertper')} placeholder="Certificate %" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Certificate %" value={profileData.ugcertper ? `${profileData.ugcertper}%` : null} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Aggregate %</Typography>
                                                <Input value={formData.ugaggrper ?? ''} onChange={handleChange('ugaggrper')} placeholder="Aggregate %" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Aggregate %" value={profileData.ugaggrper ? `${profileData.ugaggrper}%` : null} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Current Backlogs</Typography>
                                                <Input value={formData.backlog ?? ''} onChange={handleChange('backlog')} placeholder="Current Backlogs" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Current Backlogs" value={profileData.backlog ? `${profileData.backlog}` : null} />
                                        )}
                                    </Grid>

                                </Grid>
                            </Box>
                        )}

                        {/* PG Details */}
                        {(profileData.pgcollege || profileData.pgboard || profileData.pgspec || isEditing) && (
                            <Box sx={{ mb: 3 }}>
                                <Divider sx={{ my: 2 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography level="title-md" sx={{ color: '#404041', fontWeight: 'bold' }}>
                                        Post Graduation (PG)
                                    </Typography>
                                    {!profileData.pgcollegecode && (
                                        <Button
                                            size="sm"
                                            variant="outlined"
                                            color="primary"
                                            startDecorator={<SchoolIcon />}
                                            onClick={() => handleOpenCollegeModal('pg')}
                                            sx={{
                                                borderColor: '#eb7a24',
                                                color: '#eb7a24',
                                                '&:hover': {
                                                    borderColor: '#e55125',
                                                    backgroundColor: '#f19b30',
                                                    color: 'white'
                                                }
                                            }}
                                        >
                                            Update College
                                        </Button>
                                    )}
                                </Box>
                                <Grid container spacing={3}>
                                    <Grid xs={12} md={6}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Specialization</Typography>
                                                <Input value={formData.specialization ?? formData.pgspec ?? ''} onChange={handleChange('specialization')} placeholder="PG Specialization" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Specialization" value={profileData.pgspec} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} md={6}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Board/University</Typography>
                                                <Input value={formData.university ?? formData.pgboard ?? ''} onChange={handleChange('university')} placeholder="PG Board / University" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Board/University" value={profileData.pgboard} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} md={6}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>College</Typography>
                                                <Input value={formData.college ?? formData.pgcollege ?? ''} onChange={handleChange('college')} placeholder="PG College" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="College" value={profileData.pgcollege} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} md={6}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>College Address</Typography>
                                                <Textarea minRows={2} value={formData.collegeaddress ?? formData.pgcolladdr ?? ''} onChange={handleChange('collegeaddress')} placeholder="PG College Address" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="College Address" value={profileData.pgcolladdr} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>City</Typography>
                                                <Input value={formData.ccity ?? formData.pgcity ?? ''} onChange={handleChange('ccity')} placeholder="City" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="City" value={profileData.pgcity} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>State</Typography>
                                                <Input value={formData.cstate ?? formData.pgstate ?? ''} onChange={handleChange('cstate')} placeholder="State" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="State" value={profileData.pgstate} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>District</Typography>
                                                <Input value={formData.pgcollegedistrict ?? ''} onChange={handleChange('pgcollegedistrict')} placeholder="District" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="District" value={profileData.pgcollegedistrict} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Year of Passing</Typography>
                                                <Input value={formData.pgyop ?? ''} onChange={handleChange('pgyop')} placeholder="Year" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Year of Passing" value={profileData.pgyop} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>History of PG Backlogs</Typography>
                                                <Input value={formData.tpgbacklog ?? ''} onChange={handleChange('tpgbacklog')} placeholder="Total PG Backlogs" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="History of PG Backlogs" value={profileData.tpgbacklog ? `${profileData.tpgbacklog}` : null} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Site</Typography>
                                                <Input value={formData.collegesite ?? formData.pgsite ?? ''} onChange={handleChange('collegesite')} placeholder="PG College Site" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Site" value={profileData.pgsite} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Certificate %</Typography>
                                                <Input value={formData.pgcertper ?? ''} onChange={handleChange('pgcertper')} placeholder="Certificate %" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Certificate %" value={profileData.pgcertper ? `${profileData.pgcertper}%` : null} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Aggregate %</Typography>
                                                <Input value={formData.pgaggrper ?? ''} onChange={handleChange('pgaggrper')} placeholder="Aggregate %" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Aggregate %" value={profileData.pgaggrper ? `${profileData.pgaggrper}%` : null} />
                                        )}
                                    </Grid>
                                    <Grid xs={12} sm={6} md={4}>
                                        {isEditing ? (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Total Backlogs</Typography>
                                                <Input value={formData.tpgbacklog ?? ''} onChange={handleChange('tpgbacklog')} placeholder="Total Backlogs" />
                                            </Box>
                                        ) : (
                                            <FieldDisplay label="Total Backlogs" value={profileData.tpgbacklog ? `${profileData.tpgbacklog}` : null} />
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Additional Information */}
            {(profileData.project || profileData.expe || profileData.expmonth || profileData.backlog || isEditing) && (
                <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <SectionTitle title="Additional Information" icon={<WorkspacePremiumIcon sx={{ color: '#eb7a24' }} />} />
                        <Grid container spacing={3}>
                            <Grid xs={12} md={6}>
                                {isEditing ? (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Project</Typography>
                                        <Input value={formData.project} onChange={handleChange('project')} placeholder="Project" />
                                    </Box>
                                ) : (
                                    <FieldDisplay label="Project" value={profileData.project} />
                                )}
                            </Grid>
                            <Grid xs={12} sm={6} md={4}>
                                {isEditing ? (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Experience</Typography>
                                        <Input value={formData.expe} onChange={handleChange('expe')} placeholder="Experience" />
                                    </Box>
                                ) : (
                                    <FieldDisplay label="Experience" value={profileData.expe} />
                                )}
                            </Grid>
                            <Grid xs={12} sm={6} md={4}>
                                {isEditing ? (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>Experience (Months)</Typography>
                                        <Input type="number" value={formData.expmonth} onChange={handleChange('expmonth')} placeholder="Months" />
                                    </Box>
                                ) : (
                                    <FieldDisplay label="Experience (Months)" value={profileData.expmonth ? `${profileData.expmonth} months` : null} />
                                )}
                            </Grid>
                            <Grid xs={12} sm={6} md={4}>
                                {profileData.backlog && (
                                    <Box sx={{ mb: 2 }}>
                                        <Chip
                                            color={profileData.backlog > 0 ? "danger" : "success"}
                                            variant="soft"
                                            size="lg"
                                        >
                                            {profileData.backlog > 0 ? `${profileData.backlog} Active Backlog(s)` : "No Active Backlogs"}
                                        </Chip>
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* College Selection Modal */}
            <Modal open={collegeModalOpen} onClose={handleCloseCollegeModal}>
                <ModalDialog
                    sx={{
                        maxWidth: 700,
                        width: '90%',
                        height: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                        p: 0
                    }}
                >
                    <DialogTitle sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                        Update {collegeType === 'ug' ? 'UG' : 'PG'} College
                    </DialogTitle>
                    <DialogContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
                            <Input
                                placeholder="Search by college name, address, university, or website..."
                                value={collegeSearchTerm}
                                onChange={(e) => setCollegeSearchTerm(e.target.value)}
                                startDecorator={<SearchIcon />}
                                sx={{
                                    '&:hover': { borderColor: '#ea7a23' },
                                    '&:focus-within': { borderColor: '#ea7a23' }
                                }}
                            />

                            <Box
                                sx={{
                                    flex: 1,
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                    minHeight: 0
                                }}
                                onScroll={handleScroll}
                            >
                                {collegeSearchLoading && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                        <CircularProgress size="md" />
                                    </Box>
                                )}

                                {!collegeSearchLoading && collegeSearchTerm && collegeSearchResults.length === 0 && (
                                    <Alert color="neutral" variant="soft" sx={{ m: 2 }}>
                                        No colleges found. Try a different search term.
                                    </Alert>
                                )}

                                {!collegeSearchLoading && collegeSearchTerm && collegeSearchResults.length > 0 && (
                                    <List sx={{ p: 0 }}>
                                        {collegeSearchResults.map((college, index) => (
                                            <ListItem
                                                key={college.collegecode || index}
                                                sx={{
                                                    p: 0,
                                                    mb: 1,
                                                    '&:last-child': { mb: 0 }
                                                }}
                                            >
                                                <Card
                                                    variant="outlined"
                                                    sx={{
                                                        width: '100%',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            borderColor: '#eb7a24',
                                                            boxShadow: '0 2px 8px rgba(235, 122, 36, 0.2)',
                                                            transform: 'translateY(-2px)'
                                                        }
                                                    }}
                                                    onClick={() => !collegeUpdateLoading && handleSelectCollege(college)}
                                                >
                                                    <CardContent sx={{ p: 2 }}>
                                                        <Typography
                                                            level="title-lg"
                                                            sx={{
                                                                fontWeight: 'bold',
                                                                color: '#eb7a24',
                                                                mb: 1.5
                                                            }}
                                                        >
                                                            {college.collegename || 'N/A'}
                                                        </Typography>

                                                        {college.universityname && (
                                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                                                <SchoolIcon sx={{ fontSize: '18px', color: '#6e7072', mt: 0.25, flexShrink: 0 }} />
                                                                <Typography level="body-sm" sx={{ color: '#606162', flex: 1 }}>
                                                                    {college.universityname}
                                                                </Typography>
                                                            </Box>
                                                        )}

                                                        {college.collegeaddress && (
                                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                                                <LocationOnIcon sx={{ fontSize: '18px', color: '#e55125', mt: 0.25, flexShrink: 0 }} />
                                                                <Typography level="body-sm" sx={{ color: '#606162', flex: 1 }}>
                                                                    {college.collegeaddress}
                                                                </Typography>
                                                            </Box>
                                                        )}

                                                        {college.website && (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <LanguageIcon sx={{ fontSize: '18px', color: '#2196f3', flexShrink: 0 }} />
                                                                <Typography
                                                                    level="body-sm"
                                                                    sx={{
                                                                        color: '#606162',
                                                                        textDecoration: 'none',
                                                                        '&:hover': {
                                                                            textDecoration: 'underline',
                                                                            color: '#2196f3'
                                                                        }
                                                                    }}
                                                                    component="a"
                                                                    href={college.website.startsWith('http') ? college.website : `http://${college.website}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    {college.website}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            </ListItem>
                                        ))}

                                        {collegeLoadingMore && (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                                <CircularProgress size="sm" />
                                            </Box>
                                        )}

                                        {!collegeHasMore && collegeSearchResults.length > 0 && (
                                            <Box sx={{ textAlign: 'center', p: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#6e7072' }}>
                                                    No more results
                                                </Typography>
                                            </Box>
                                        )}
                                    </List>
                                )}

                                {!collegeSearchTerm && (
                                    <Alert color="info" variant="soft" sx={{ m: 2 }}>
                                        Start typing to search for colleges...
                                    </Alert>
                                )}
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Button
                                    variant="outlined"
                                    color="neutral"
                                    onClick={handleCloseCollegeModal}
                                    disabled={collegeUpdateLoading}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </Box>
                    </DialogContent>
                </ModalDialog>
            </Modal>
        </Box>
    );
};

export default Profile;

